import { useState, useEffect, useCallback } from 'react';
import { getImagePathWithSize, ImageSize } from '../../../Helpers/ImageLoader';

export interface GuestThumbnail {
    /** Resolved URL to render in the tile (thumbnail, or full-size fallback). */
    url: string | null;
    loading: boolean;
    error: boolean;
    /**
     * Wire to the tile's `<img onError>`. A freshly uploaded photo may not have
     * its thumbnail generated yet (generation is async), so the suffixed object
     * 404s — this swaps in the full-size original once before giving up.
     */
    onImgError: () => void;
}

/**
 * Resolves a small thumbnail URL for a guest image tile so lists load fast
 * instead of pulling full-size originals. The full-size original is fetched
 * separately, on demand, when the tile is opened (see the section-level viewer).
 *
 * Falls back to the full-size original when the thumbnail can't be served:
 * - the guest token isn't authorized for the suffixed path (server rejects the
 *   thumbnail variant — e.g. work-order attachments validated by exact path), or
 * - the thumbnail object doesn't exist yet (fresh upload; the `<img>` 404s).
 *
 * With the fallback, this never regresses a case that works today: worst case it
 * loads the same full-size original it loaded before.
 */
export function useGuestThumbnail(
    storagePath: string,
    onGetDownloadUrl: (storagePath: string) => Promise<string>,
    size: ImageSize = ImageSize.THUMBNAIL_SMALL,
): GuestThumbnail {
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    // Guards the thumbnail→full-size fallback so we only retry the original once.
    const [triedFullSize, setTriedFullSize] = useState(false);

    const thumbnailPath = getImagePathWithSize(storagePath, size) || storagePath;

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(false);
        setTriedFullSize(false);

        onGetDownloadUrl(thumbnailPath)
            .then((downloadUrl) => {
                if (mounted) {
                    setUrl(downloadUrl);
                    setLoading(false);
                }
            })
            .catch(() => {
                // Thumbnail path rejected (or network error). If there is no
                // distinct thumbnail path, this is already the original — error.
                if (thumbnailPath === storagePath) {
                    if (mounted) {
                        setError(true);
                        setLoading(false);
                    }
                    return;
                }
                // Otherwise fall back to the full-size original.
                onGetDownloadUrl(storagePath)
                    .then((downloadUrl) => {
                        if (mounted) {
                            setUrl(downloadUrl);
                            setTriedFullSize(true);
                            setLoading(false);
                        }
                    })
                    .catch(() => {
                        if (mounted) {
                            setError(true);
                            setLoading(false);
                        }
                    });
            });

        return () => {
            mounted = false;
        };
    }, [thumbnailPath, storagePath, onGetDownloadUrl]);

    const onImgError = useCallback(() => {
        if (triedFullSize) {
            setError(true);
            return;
        }
        setTriedFullSize(true);
        onGetDownloadUrl(storagePath)
            .then((downloadUrl) => setUrl(downloadUrl))
            .catch(() => setError(true));
    }, [triedFullSize, storagePath, onGetDownloadUrl]);

    return { url, loading, error, onImgError };
}
